<template>
  <section
    class="sp-finder-result-other-locations"
    :aria-label="ariaLabel"
    :aria-busy="loading"
    :hidden="hidden"
    :data-testid="TestId.finder.otherLocationsRegion"
  >
    <div v-if="loading" class="sp-finder-result-other-locations__status" role="status">
      <sp-progress type="ellipsis" size="x-small" :aria-label="t('results.other_locations_loading')"></sp-progress>
    </div>
    <div v-else-if="failed" class="sp-finder-result-other-locations__status" role="alert">
      <span>{{ t("results.other_locations_error") }}</span>
      <sp-button variant="appearance-link" :data-testid="TestId.finder.otherLocationsRetry" @click="handleRetry">{{
        t("results.other_locations_retry")
      }}</sp-button>
    </div>
    <div v-for="other in otherLocations.results" :key="other.resultGUID" class="sp-finder-result-other-locations__row">
      <FinderResultAddress
        class="sp-finder-result-other-locations__address"
        layout="bio"
        :result="other"
        :result-type="resultType"
        :result-config="resultConfig"
        :labels="labels"
      />
      <FinderResultScheduling
        class="sp-finder-result-other-locations__scheduling"
        :result="other"
        :scheduling="scheduling"
        @show-more-slots="handleShowMoreSlots"
        @show-date="handleShowDate"
      />
    </div>
  </section>
</template>

<script setup>
import { computed } from "vue";
import { TestId } from "#/test-ids";
import { i18n } from "#ce/components/SpFinder/i18n";
import { OtherLocationsStatus } from "#ce/components/SpFinder/view-models/other-locations-status";
import FinderResultAddress from "./FinderResultAddress.ce.vue";
import FinderResultScheduling from "./FinderResultScheduling.ce.vue";

function t(...args) {
  return i18n.global.t(...args);
}

const emit = defineEmits(["retry", "show-more-slots", "show-date"]);

const props = defineProps({
  /** The fold-out state: `{ count, expanded, status, results }`, `results` being one result per other address. */
  otherLocations: {
    type: Object,
    required: true,
  },
  /** The provider the region lists other locations for. */
  providerName: {
    type: String,
    default: "",
  },
  resultType: {
    type: String,
    default: "provider",
  },
  resultConfig: {
    type: Object,
    default: undefined,
  },
  labels: {
    type: Object,
    default: () => ({}),
  },
  scheduling: {
    type: Object,
    default: () => ({}),
  },
});

const ariaLabel = computed(() => t("results.other_locations_region", { name: props.providerName }));
const hidden = computed(() => !props.otherLocations.expanded);
const status = computed(() => props.otherLocations.status);
const loading = computed(() => status.value === OtherLocationsStatus.LOADING);
const failed = computed(() => status.value === OtherLocationsStatus.ERROR);

function handleRetry() {
  emit("retry");
}

function handleShowMoreSlots(payload) {
  emit("show-more-slots", payload);
}

function handleShowDate(date) {
  emit("show-date", date);
}
</script>

<style lang="scss" scoped>
.sp-finder-result-other-locations {
  --other-locations-divider: var(--sp-comp-finder-results-divider, 1px solid var(--sp-sys-color-outline-variant));
  --other-locations-row-padding-block: var(
    --sp-comp-finder-result-other-locations-row-padding-block,
    var(--sp-ref-scale-24)
  );

  display: grid;
  grid-template-columns: subgrid;

  &[hidden] {
    display: none;
  }

  &__status {
    display: flex;
    grid-column: 1 / -1;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--sp-ref-scale-8);
    padding-block: var(--other-locations-row-padding-block);
    border-top: var(--other-locations-divider);
  }

  &__row {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: subgrid;
    row-gap: var(--sp-ref-scale-24);
    padding-block: var(--other-locations-row-padding-block);
    border-top: var(--other-locations-divider);
  }

  &__address,
  &__scheduling {
    min-width: 0;
  }
}
</style>
